import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { 
  extractPdfText, editPdfTextInline, renderPdfPreview, 
  getPdfPreviewUrl, downloadModifiedPdf 
} from '../../lib/api';
import {
  Upload, Download, Loader2, Check, ZoomIn, ZoomOut, 
  ChevronLeft, ChevronRight, RefreshCw, Save, Edit3, X, Move, Palette
} from 'lucide-react';

export default function PdfVisualEditor() {
  // File state
  const [pdfFile, setPdfFile] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [pageInfo, setPageInfo] = useState(null);
  const [textItems, setTextItems] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [zoom, setZoom] = useState(1.5);
  
  // Edit state
  const [editingItem, setEditingItem] = useState(null);
  const [editedItems, setEditedItems] = useState({});
  const [editValue, setEditValue] = useState('');
  const [editColor, setEditColor] = useState('#000000');
  const [editFontSize, setEditFontSize] = useState(12);
  const [editFontFamily, setEditFontFamily] = useState('Helvetica');
  const [editBold, setEditBold] = useState(false);
  const [editItalic, setEditItalic] = useState(false);
  const [editUnderline, setEditUnderline] = useState(false);
  
  // Saved colors - stored in localStorage
  const [savedColors, setSavedColors] = useState(() => {
    try {
      const saved = localStorage.getItem('pdfEditor_savedColors');
      return saved ? JSON.parse(saved) : ['#000000', '#1a5276', '#c0392b', '#27ae60'];
    } catch {
      return ['#000000', '#1a5276', '#c0392b', '#27ae60'];
    }
  });
  
  const [recentColors, setRecentColors] = useState(() => {
    try {
      const recent = localStorage.getItem('pdfEditor_recentColors');
      return recent ? JSON.parse(recent) : [];
    } catch {
      return [];
    }
  });
  
  // Save color to favorites
  const saveColorToFavorites = (color) => {
    if (!savedColors.includes(color)) {
      const newSaved = [color, ...savedColors].slice(0, 12); // Max 12 saved colors
      setSavedColors(newSaved);
      localStorage.setItem('pdfEditor_savedColors', JSON.stringify(newSaved));
      toast.success('تم حفظ اللون في المفضلة');
    }
  };
  
  // Add to recent colors
  const addToRecentColors = (color) => {
    const filtered = recentColors.filter(c => c !== color);
    const newRecent = [color, ...filtered].slice(0, 8); // Max 8 recent colors
    setRecentColors(newRecent);
    localStorage.setItem('pdfEditor_recentColors', JSON.stringify(newRecent));
  };
  
  // Remove from saved colors
  const removeFromSaved = (color) => {
    const newSaved = savedColors.filter(c => c !== color);
    setSavedColors(newSaved);
    localStorage.setItem('pdfEditor_savedColors', JSON.stringify(newSaved));
  };
  
  // Available fonts
  const fontFamilies = [
    'Helvetica', 'Arial', 'Times New Roman', 'Courier', 'Georgia', 
    'Verdana', 'Tahoma', 'Trebuchet MS', 'Impact', 'Comic Sans MS'
  ];
  
  // Font sizes
  const fontSizes = [8, 9, 10, 11, 12, 14, 16, 18, 20, 22, 24, 26, 28, 32, 36, 48, 72];
  
  // Drag state
  const [draggingItem, setDraggingItem] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  
  // Refs
  const pdfInputRef = useRef(null);
  const editInputRef = useRef(null);
  const containerRef = useRef(null);

  // Handle PDF file selection
  const handlePdfSelect = async (e) => {
    const file = e.target.files[0];
    if (!file || !file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('يجب أن يكون الملف PDF');
      return;
    }
    
    setPdfFile(file);
    setEditedItems({});
    setResult(null);
    await loadPdfContent(file, 1);
  };

  // Load PDF content (preview + text extraction)
  const loadPdfContent = async (file, pageNum) => {
    setLoading(true);
    
    try {
      // Render preview image
      const previewFormData = new FormData();
      previewFormData.append('pdf_file', file);
      previewFormData.append('page_number', pageNum);
      previewFormData.append('zoom', zoom);
      
      const previewRes = await renderPdfPreview(previewFormData);
      if (previewRes.data.success) {
        setPreviewImage(getPdfPreviewUrl(previewRes.data.image_name));
        setPageInfo({
          width: previewRes.data.width / zoom,
          height: previewRes.data.height / zoom
        });
      }
      
      // Extract text
      const textFormData = new FormData();
      textFormData.append('pdf_file', file);
      textFormData.append('page_number', pageNum);
      
      const textRes = await extractPdfText(textFormData);
      if (textRes.data.success) {
        setTextItems(textRes.data.text_items);
        setTotalPages(textRes.data.total_pages);
      }
      
    } catch (error) {
      console.error('Error loading PDF:', error);
      toast.error('فشل تحميل ملف PDF');
    } finally {
      setLoading(false);
    }
  };

  // Handle text item click for editing
  const handleTextClick = (item, e) => {
    if (draggingItem) return; // Don't open edit if dragging
    e.stopPropagation();
    
    const currentEdit = editedItems[item.id];
    setEditingItem(item);
    setEditValue(currentEdit?.new_text || item.text);
    setEditColor(currentEdit?.color || item.color || '#000000');
    setEditFontSize(currentEdit?.font_size || item.font_size || 12);
    setEditFontFamily(currentEdit?.font_family || item.font_name || 'Helvetica');
    setEditBold(currentEdit?.is_bold ?? item.is_bold ?? false);
    setEditItalic(currentEdit?.is_italic ?? false);
    setEditUnderline(currentEdit?.is_underline ?? false);
    setTimeout(() => editInputRef.current?.focus(), 100);
  };

  // Save edit
  const saveEdit = () => {
    if (!editingItem) return;
    
    const currentEdit = editedItems[editingItem.id] || {};
    const hasTextChange = editValue !== editingItem.text;
    const hasColorChange = editColor !== (editingItem.color || '#000000');
    const hasFontSizeChange = editFontSize !== (editingItem.font_size || 12);
    const hasFontFamilyChange = editFontFamily !== (editingItem.font_name || 'Helvetica');
    const hasBoldChange = editBold !== (editingItem.is_bold || false);
    const hasItalicChange = editItalic !== false;
    const hasUnderlineChange = editUnderline !== false;
    const hasPositionChange = currentEdit.display_x !== undefined || currentEdit.display_y !== undefined;
    
    const hasAnyChange = hasTextChange || hasColorChange || hasFontSizeChange || 
                         hasFontFamilyChange || hasBoldChange || hasItalicChange || 
                         hasUnderlineChange || hasPositionChange;
    
    if (hasAnyChange) {
      // Add color to recent colors
      addToRecentColors(editColor);
      
      setEditedItems({
        ...editedItems,
        [editingItem.id]: {
          ...editingItem,
          ...currentEdit,
          new_text: editValue,
          color: editColor,
          font_size: editFontSize,
          font_family: editFontFamily,
          is_bold: editBold,
          is_italic: editItalic,
          is_underline: editUnderline,
          // Store original position for covering old text when moving
          original_display_x: editingItem.display_x,
          original_display_y: editingItem.display_y,
          original_width: editingItem.display_width,
          original_height: editingItem.display_height
        }
      });
    } else {
      // Remove if reverted to original
      const newEdited = { ...editedItems };
      delete newEdited[editingItem.id];
      setEditedItems(newEdited);
    }
    
    setEditingItem(null);
    setEditValue('');
  };

  // Cancel edit
  const cancelEdit = () => {
    setEditingItem(null);
    setEditValue('');
  };

  // Start dragging
  const handleDragStart = (item, e) => {
    e.stopPropagation();
    e.preventDefault();
    
    const container = containerRef.current;
    if (!container) return;
    
    const rect = container.getBoundingClientRect();
    const scrollLeft = container.scrollLeft;
    const scrollTop = container.scrollTop;
    
    // Get current position (either edited or original)
    const currentEdit = editedItems[item.id];
    const currentX = (currentEdit?.display_x ?? item.display_x) * zoom;
    const currentY = (currentEdit?.display_y ?? item.display_y) * zoom;
    
    setDragOffset({
      x: e.clientX - rect.left + scrollLeft - currentX,
      y: e.clientY - rect.top + scrollTop - currentY
    });
    setDraggingItem(item);
  };

  // Handle drag move
  const handleDragMove = (e) => {
    if (!draggingItem || !containerRef.current) return;
    
    const container = containerRef.current;
    const rect = container.getBoundingClientRect();
    const scrollLeft = container.scrollLeft;
    const scrollTop = container.scrollTop;
    
    // Calculate new position
    const newX = (e.clientX - rect.left + scrollLeft - dragOffset.x) / zoom;
    const newY = (e.clientY - rect.top + scrollTop - dragOffset.y) / zoom;
    
    // Update position in editedItems - preserve original position for backend
    const currentEdit = editedItems[draggingItem.id] || {};
    setEditedItems({
      ...editedItems,
      [draggingItem.id]: {
        ...draggingItem,
        ...currentEdit,
        display_x: Math.max(0, newX),
        display_y: Math.max(0, newY),
        new_text: currentEdit.new_text || draggingItem.text,
        // Store original position (only set once, when first moved)
        original_display_x: currentEdit.original_display_x ?? draggingItem.display_x,
        original_display_y: currentEdit.original_display_y ?? draggingItem.display_y,
        original_width: currentEdit.original_width ?? draggingItem.display_width,
        original_height: currentEdit.original_height ?? draggingItem.display_height
      }
    });
  };

  // End dragging
  const handleDragEnd = () => {
    if (draggingItem) {
      toast.success('تم نقل النص');
    }
    setDraggingItem(null);
    setDragOffset({ x: 0, y: 0 });
  };

  // Mouse event listeners for dragging
  useEffect(() => {
    if (draggingItem) {
      const handleMouseMove = (e) => handleDragMove(e);
      const handleMouseUp = () => handleDragEnd();
      
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [draggingItem, dragOffset, editedItems, zoom]);

  // Apply all edits
  const applyEdits = async () => {
    if (!pdfFile || Object.keys(editedItems).length === 0) {
      toast.error('لا توجد تعديلات للتطبيق');
      return;
    }
    
    setProcessing(true);
    
    try {
      const formData = new FormData();
      formData.append('pdf_file', pdfFile);
      formData.append('edits', JSON.stringify(Object.values(editedItems)));
      formData.append('page_number', currentPage);
      
      const response = await editPdfTextInline(formData);
      
      if (response.data.success) {
        setResult(response.data);
        toast.success(`تم تعديل ${response.data.edits_applied} نصوص بنجاح!`);
      }
    } catch (error) {
      console.error('Error applying edits:', error);
      toast.error(error.response?.data?.detail || 'فشل تطبيق التعديلات');
    } finally {
      setProcessing(false);
    }
  };

  // Download result
  const handleDownload = async () => {
    if (!result?.file_name) return;
    
    try {
      const response = await fetch(downloadModifiedPdf(result.file_name));
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `edited_${Date.now()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('تم تحميل الملف');
    } catch (error) {
      toast.error('فشل التحميل');
    }
  };

  // Change page
  const changePage = async (newPage) => {
    if (newPage < 1 || newPage > totalPages || !pdfFile) return;
    setCurrentPage(newPage);
    await loadPdfContent(pdfFile, newPage);
  };

  // Change zoom
  const changeZoom = async (newZoom) => {
    setZoom(newZoom);
    if (pdfFile) {
      await loadPdfContent(pdfFile, currentPage);
    }
  };

  // Reset
  const handleReset = () => {
    setPdfFile(null);
    setPreviewImage(null);
    setTextItems([]);
    setEditedItems({});
    setResult(null);
    setEditingItem(null);
    if (pdfInputRef.current) pdfInputRef.current.value = '';
  };

  // Handle keyboard
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (editingItem) {
        if (e.key === 'Enter') {
          e.preventDefault();
          saveEdit();
        } else if (e.key === 'Escape') {
          cancelEdit();
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [editingItem, editValue]);

  const editCount = Object.keys(editedItems).length;

  return (
    <div className="space-y-4">
      {/* Controls Bar */}
      <div className="flex flex-wrap items-center gap-3 p-4 bg-card rounded-lg border">
        <input type="file" ref={pdfInputRef} className="hidden" accept=".pdf" onChange={handlePdfSelect} />
        
        <Button variant="outline" onClick={() => pdfInputRef.current?.click()} data-testid="pdf-visual-upload-btn">
          <Upload className="h-4 w-4 mr-2" />
          {pdfFile ? pdfFile.name : 'اختر ملف PDF'}
        </Button>
        
        {previewImage && (
          <>
            <div className="h-6 w-px bg-border" />
            
            {/* Zoom controls */}
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" onClick={() => changeZoom(Math.max(0.5, zoom - 0.25))} disabled={loading}>
                <ZoomOut className="h-4 w-4" />
              </Button>
              <span className="text-sm w-16 text-center">{Math.round(zoom * 100)}%</span>
              <Button variant="ghost" size="icon" onClick={() => changeZoom(Math.min(3, zoom + 0.25))} disabled={loading}>
                <ZoomIn className="h-4 w-4" />
              </Button>
            </div>
            
            {/* Page navigation */}
            {totalPages > 1 && (
              <>
                <div className="h-6 w-px bg-border" />
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon" onClick={() => changePage(currentPage - 1)} disabled={currentPage <= 1 || loading}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  <span className="text-sm w-24 text-center">صفحة {currentPage} / {totalPages}</span>
                  <Button variant="ghost" size="icon" onClick={() => changePage(currentPage + 1)} disabled={currentPage >= totalPages || loading}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                </div>
              </>
            )}
            
            <div className="flex-1" />
            
            {editCount > 0 && (
              <span className="text-sm bg-primary/10 text-primary px-3 py-1 rounded-full">
                {editCount} تعديل
              </span>
            )}
            
            <Button variant="outline" onClick={handleReset}>
              <RefreshCw className="h-4 w-4 mr-2" />
              جديد
            </Button>
            
            <Button 
              onClick={applyEdits} 
              disabled={editCount === 0 || processing}
              data-testid="save-edits-btn"
            >
              {processing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              حفظ التعديلات ({editCount})
            </Button>
          </>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* PDF Preview with Clickable Text */}
        <div className="lg:col-span-3">
          <Card className="bg-card">
            <CardContent className="p-4">
              {!pdfFile ? (
                <div 
                  className="h-[600px] border-2 border-dashed rounded-lg flex items-center justify-center cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => pdfInputRef.current?.click()}
                >
                  <div className="text-center">
                    <Upload className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-lg font-medium">اضغط لاختيار ملف PDF</p>
                    <p className="text-sm text-muted-foreground mt-2">انقر على أي نص لتعديله مباشرة</p>
                  </div>
                </div>
              ) : loading ? (
                <div className="h-[600px] flex items-center justify-center">
                  <Loader2 className="h-8 w-8 animate-spin" />
                  <span className="mr-2">جاري تحميل الملف...</span>
                </div>
              ) : (
                <div 
                  ref={containerRef}
                  className="relative overflow-auto max-h-[700px] bg-gray-200 rounded-lg" 
                  style={{ direction: 'ltr', cursor: draggingItem ? 'grabbing' : 'default' }}
                >
                  {/* PDF Preview Image */}
                  <div className="relative inline-block">
                    <img 
                      src={previewImage} 
                      alt="PDF Preview" 
                      className="block"
                      style={{ maxWidth: 'none' }}
                    />
                    
                    {/* Clickable Text Overlays */}
                    {textItems.map(item => {
                      const isEdited = editedItems[item.id];
                      const isEditing = editingItem?.id === item.id;
                      const isDragging = draggingItem?.id === item.id;
                      
                      // Use edited position if available
                      const displayX = (isEdited?.display_x ?? item.display_x) * zoom;
                      const displayY = (isEdited?.display_y ?? item.display_y) * zoom;
                      const displayColor = isEdited?.color || item.color || '#000000';
                      
                      return (
                        <div
                          key={item.id}
                          data-testid={`text-item-${item.id}`}
                          className={`absolute transition-all group ${
                            isDragging
                              ? 'z-50 cursor-grabbing opacity-80'
                              : isEditing 
                                ? 'z-50' 
                                : isEdited 
                                  ? 'bg-green-500/30 border-2 border-green-500 hover:bg-green-500/40 cursor-pointer' 
                                  : 'bg-blue-500/10 border border-dashed border-blue-400 hover:bg-blue-500/30 hover:border-solid hover:border-blue-600 cursor-pointer'
                          }`}
                          style={{
                            left: displayX,
                            top: displayY,
                            width: item.display_width * zoom + 4,
                            height: item.display_height * zoom + 2,
                            minWidth: '20px',
                          }}
                          onClick={(e) => !isEditing && !draggingItem && handleTextClick(item, e)}
                          title={isEdited ? `تم التعديل: ${isEdited.new_text}` : `انقر للتعديل أو اسحب للنقل: ${item.text}`}
                        >
                          {/* Drag handle */}
                          {!isEditing && (
                            <div 
                              className="absolute -top-6 left-0 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1"
                            >
                              <button
                                className="bg-blue-600 text-white text-xs px-1.5 py-0.5 rounded cursor-grab hover:bg-blue-700 flex items-center gap-1"
                                onMouseDown={(e) => handleDragStart(item, e)}
                                title="اسحب لنقل النص"
                              >
                                <Move className="h-3 w-3" />
                                نقل
                              </button>
                            </div>
                          )}
                          
                          {/* Edit popup */}
                          {isEditing && (
                            <div 
                              className="absolute bg-white shadow-xl rounded-lg border-2 border-primary p-3"
                              style={{
                                top: -8,
                                left: -8,
                                minWidth: '400px',
                                maxWidth: '450px',
                                zIndex: 100
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {/* Font Toolbar - Like Word */}
                              <div className="mb-3 p-2 bg-gray-50 rounded-lg border">
                                <div className="flex flex-wrap items-center gap-2 mb-2">
                                  {/* Font Family */}
                                  <select
                                    value={editFontFamily}
                                    onChange={(e) => setEditFontFamily(e.target.value)}
                                    className="h-8 px-2 text-sm border rounded bg-white min-w-[120px]"
                                  >
                                    {fontFamilies.map(font => (
                                      <option key={font} value={font} style={{ fontFamily: font }}>
                                        {font}
                                      </option>
                                    ))}
                                  </select>
                                  
                                  {/* Font Size */}
                                  <div className="flex items-center border rounded bg-white">
                                    <button
                                      onClick={() => setEditFontSize(Math.max(6, editFontSize - 1))}
                                      className="h-8 w-8 flex items-center justify-center hover:bg-gray-100 border-l"
                                      title="تصغير"
                                    >
                                      <span className="text-xs font-bold">A-</span>
                                    </button>
                                    <select
                                      value={editFontSize}
                                      onChange={(e) => setEditFontSize(Number(e.target.value))}
                                      className="h-8 w-14 text-center text-sm bg-white border-0"
                                    >
                                      {fontSizes.map(size => (
                                        <option key={size} value={size}>{size}</option>
                                      ))}
                                    </select>
                                    <button
                                      onClick={() => setEditFontSize(Math.min(72, editFontSize + 1))}
                                      className="h-8 w-8 flex items-center justify-center hover:bg-gray-100 border-r"
                                      title="تكبير"
                                    >
                                      <span className="text-sm font-bold">A+</span>
                                    </button>
                                  </div>
                                </div>
                                
                                {/* Text Style Buttons */}
                                <div className="flex items-center gap-1">
                                  {/* Bold */}
                                  <button
                                    onClick={() => setEditBold(!editBold)}
                                    className={`h-8 w-8 flex items-center justify-center rounded border ${editBold ? 'bg-blue-500 text-white border-blue-600' : 'bg-white hover:bg-gray-100 border-gray-300'}`}
                                    title="عريض (Bold)"
                                  >
                                    <span className="font-bold">B</span>
                                  </button>
                                  
                                  {/* Italic */}
                                  <button
                                    onClick={() => setEditItalic(!editItalic)}
                                    className={`h-8 w-8 flex items-center justify-center rounded border ${editItalic ? 'bg-blue-500 text-white border-blue-600' : 'bg-white hover:bg-gray-100 border-gray-300'}`}
                                    title="مائل (Italic)"
                                  >
                                    <span className="italic font-serif">I</span>
                                  </button>
                                  
                                  {/* Underline */}
                                  <button
                                    onClick={() => setEditUnderline(!editUnderline)}
                                    className={`h-8 w-8 flex items-center justify-center rounded border ${editUnderline ? 'bg-blue-500 text-white border-blue-600' : 'bg-white hover:bg-gray-100 border-gray-300'}`}
                                    title="تحته خط (Underline)"
                                  >
                                    <span className="underline">U</span>
                                  </button>
                                  
                                  <div className="w-px h-6 bg-gray-300 mx-1"></div>
                                  
                                  {/* Font Color - Full Color Picker */}
                                  <div className="flex items-center gap-1">
                                    <input
                                      type="color"
                                      value={editColor}
                                      onChange={(e) => setEditColor(e.target.value)}
                                      className="w-10 h-8 rounded border cursor-pointer"
                                      title="اختر أي لون - انقر هنا"
                                    />
                                    <span className="text-sm font-bold" style={{ color: editColor }}>A</span>
                                    
                                    {/* Save current color button */}
                                    <button
                                      onClick={() => saveColorToFavorites(editColor)}
                                      className="h-8 px-2 text-xs rounded border bg-white hover:bg-gray-100 border-gray-300 flex items-center gap-1"
                                      title="حفظ اللون الحالي في المفضلة"
                                    >
                                      ⭐ حفظ
                                    </button>
                                  </div>
                                </div>
                                
                                {/* Saved Colors */}
                                {savedColors.length > 0 && (
                                  <div className="flex items-center gap-2 mt-2">
                                    <span className="text-xs text-muted-foreground">المفضلة:</span>
                                    <div className="flex gap-1 flex-wrap">
                                      {savedColors.map((color, index) => (
                                        <div key={`saved-${index}`} className="relative group">
                                          <button
                                            onClick={() => setEditColor(color)}
                                            className={`w-6 h-6 rounded border-2 ${editColor === color ? 'ring-2 ring-primary' : 'border-gray-300'}`}
                                            style={{ backgroundColor: color }}
                                            title={`استخدام ${color}`}
                                          />
                                          <button
                                            onClick={() => removeFromSaved(color)}
                                            className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 text-white rounded-full text-xs opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                                            title="حذف من المفضلة"
                                          >
                                            ×
                                          </button>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                
                                {/* Recent Colors */}
                                {recentColors.length > 0 && (
                                  <div className="flex items-center gap-2 mt-2">
                                    <span className="text-xs text-muted-foreground">الأخيرة:</span>
                                    <div className="flex gap-1">
                                      {recentColors.map((color, index) => (
                                        <button
                                          key={`recent-${index}`}
                                          onClick={() => setEditColor(color)}
                                          className={`w-5 h-5 rounded border ${editColor === color ? 'ring-2 ring-primary' : 'border-gray-300'}`}
                                          style={{ backgroundColor: color }}
                                          title={color}
                                        />
                                      ))}
                                    </div>
                                  </div>
                                )}
                                
                                {/* Color code input */}
                                <div className="flex items-center gap-2 mt-2">
                                  <span className="text-xs text-muted-foreground">كود اللون:</span>
                                  <Input
                                    value={editColor}
                                    onChange={(e) => setEditColor(e.target.value)}
                                    className="w-24 h-7 text-xs font-mono"
                                    placeholder="#000000"
                                  />
                                  <div 
                                    className="w-7 h-7 rounded border-2 border-gray-400"
                                    style={{ backgroundColor: editColor }}
                                  ></div>
                                </div>
                              </div>
                              
                              {/* Text Input */}
                              <div className="mb-3">
                                <Label className="text-xs mb-1 block">النص:</Label>
                                <Input
                                  ref={editInputRef}
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  style={{
                                    fontSize: `${Math.min(24, editFontSize)}px`,
                                    color: editColor,
                                    fontFamily: editFontFamily,
                                    fontWeight: editBold ? 'bold' : 'normal',
                                    fontStyle: editItalic ? 'italic' : 'normal',
                                    textDecoration: editUnderline ? 'underline' : 'none'
                                  }}
                                  className="text-lg"
                                  autoFocus
                                />
                              </div>
                              
                              {/* Extended Color Palette */}
                              <details className="mb-3">
                                <summary className="text-xs text-muted-foreground cursor-pointer hover:text-primary flex items-center gap-1">
                                  <Palette className="h-3 w-3" />
                                  لوحة الألوان الكاملة (انقر للفتح)
                                </summary>
                                <div className="mt-2 p-2 bg-gray-50 rounded border">
                                  {/* Grayscale */}
                                  <div className="mb-2">
                                    <span className="text-xs text-muted-foreground block mb-1">تدرج الرمادي:</span>
                                    <div className="flex gap-1 flex-wrap">
                                      {['#000000', '#111111', '#222222', '#333333', '#444444', '#555555', '#666666', '#777777', '#888888', '#999999', '#aaaaaa', '#bbbbbb', '#cccccc', '#dddddd', '#eeeeee', '#ffffff'].map(color => (
                                        <button
                                          key={color}
                                          onClick={() => setEditColor(color)}
                                          className={`w-6 h-6 rounded border ${editColor === color ? 'ring-2 ring-primary' : 'border-gray-300'}`}
                                          style={{ backgroundColor: color }}
                                          title={color}
                                        />
                                      ))}
                                    </div>
                                  </div>
                                  
                                  {/* Reds */}
                                  <div className="mb-2">
                                    <span className="text-xs text-muted-foreground block mb-1">الأحمر:</span>
                                    <div className="flex gap-1 flex-wrap">
                                      {['#ffcccc', '#ff9999', '#ff6666', '#ff3333', '#ff0000', '#cc0000', '#990000', '#660000', '#330000', '#c0392b', '#e74c3c', '#cd6155', '#f1948a', '#fadbd8'].map(color => (
                                        <button
                                          key={color}
                                          onClick={() => setEditColor(color)}
                                          className={`w-6 h-6 rounded border ${editColor === color ? 'ring-2 ring-primary' : 'border-gray-300'}`}
                                          style={{ backgroundColor: color }}
                                          title={color}
                                        />
                                      ))}
                                    </div>
                                  </div>
                                  
                                  {/* Blues */}
                                  <div className="mb-2">
                                    <span className="text-xs text-muted-foreground block mb-1">الأزرق:</span>
                                    <div className="flex gap-1 flex-wrap">
                                      {['#ccccff', '#9999ff', '#6666ff', '#3333ff', '#0000ff', '#0000cc', '#000099', '#000066', '#000033', '#1a5276', '#2980b9', '#3498db', '#5dade2', '#85c1e9', '#aed6f1'].map(color => (
                                        <button
                                          key={color}
                                          onClick={() => setEditColor(color)}
                                          className={`w-6 h-6 rounded border ${editColor === color ? 'ring-2 ring-primary' : 'border-gray-300'}`}
                                          style={{ backgroundColor: color }}
                                          title={color}
                                        />
                                      ))}
                                    </div>
                                  </div>
                                  
                                  {/* Greens */}
                                  <div className="mb-2">
                                    <span className="text-xs text-muted-foreground block mb-1">الأخضر:</span>
                                    <div className="flex gap-1 flex-wrap">
                                      {['#ccffcc', '#99ff99', '#66ff66', '#33ff33', '#00ff00', '#00cc00', '#009900', '#006600', '#003300', '#1e8449', '#27ae60', '#2ecc71', '#58d68d', '#82e0aa', '#abebc6'].map(color => (
                                        <button
                                          key={color}
                                          onClick={() => setEditColor(color)}
                                          className={`w-6 h-6 rounded border ${editColor === color ? 'ring-2 ring-primary' : 'border-gray-300'}`}
                                          style={{ backgroundColor: color }}
                                          title={color}
                                        />
                                      ))}
                                    </div>
                                  </div>
                                  
                                  {/* Yellows & Oranges */}
                                  <div className="mb-2">
                                    <span className="text-xs text-muted-foreground block mb-1">الأصفر والبرتقالي:</span>
                                    <div className="flex gap-1 flex-wrap">
                                      {['#ffffcc', '#ffff99', '#ffff66', '#ffff33', '#ffff00', '#ffcc00', '#ff9900', '#ff6600', '#ff3300', '#f39c12', '#f1c40f', '#f4d03f', '#f7dc6f', '#f9e79f', '#fcf3cf'].map(color => (
                                        <button
                                          key={color}
                                          onClick={() => setEditColor(color)}
                                          className={`w-6 h-6 rounded border ${editColor === color ? 'ring-2 ring-primary' : 'border-gray-300'}`}
                                          style={{ backgroundColor: color }}
                                          title={color}
                                        />
                                      ))}
                                    </div>
                                  </div>
                                  
                                  {/* Purples & Pinks */}
                                  <div className="mb-2">
                                    <span className="text-xs text-muted-foreground block mb-1">البنفسجي والوردي:</span>
                                    <div className="flex gap-1 flex-wrap">
                                      {['#ffccff', '#ff99ff', '#ff66ff', '#ff33ff', '#ff00ff', '#cc00cc', '#990099', '#660066', '#330033', '#8e44ad', '#9b59b6', '#a569bd', '#bb8fce', '#d7bde2', '#ff69b4', '#ffb6c1'].map(color => (
                                        <button
                                          key={color}
                                          onClick={() => setEditColor(color)}
                                          className={`w-6 h-6 rounded border ${editColor === color ? 'ring-2 ring-primary' : 'border-gray-300'}`}
                                          style={{ backgroundColor: color }}
                                          title={color}
                                        />
                                      ))}
                                    </div>
                                  </div>
                                  
                                  {/* Browns & Tans */}
                                  <div className="mb-2">
                                    <span className="text-xs text-muted-foreground block mb-1">البني والبيج:</span>
                                    <div className="flex gap-1 flex-wrap">
                                      {['#4e342e', '#5d4037', '#6d4c41', '#795548', '#8d6e63', '#a1887f', '#bcaaa4', '#d7ccc8', '#efebe9', '#c9a66b', '#daa520', '#b8860b', '#cd853f', '#d2691e', '#8b4513', '#a52a2a'].map(color => (
                                        <button
                                          key={color}
                                          onClick={() => setEditColor(color)}
                                          className={`w-6 h-6 rounded border ${editColor === color ? 'ring-2 ring-primary' : 'border-gray-300'}`}
                                          style={{ backgroundColor: color }}
                                          title={color}
                                        />
                                      ))}
                                    </div>
                                  </div>
                                  
                                  {/* Cyans & Teals */}
                                  <div>
                                    <span className="text-xs text-muted-foreground block mb-1">السماوي:</span>
                                    <div className="flex gap-1 flex-wrap">
                                      {['#ccffff', '#99ffff', '#66ffff', '#33ffff', '#00ffff', '#00cccc', '#009999', '#006666', '#003333', '#16a085', '#1abc9c', '#48c9b0', '#76d7c4', '#a3e4d7', '#d1f2eb'].map(color => (
                                        <button
                                          key={color}
                                          onClick={() => setEditColor(color)}
                                          className={`w-6 h-6 rounded border ${editColor === color ? 'ring-2 ring-primary' : 'border-gray-300'}`}
                                          style={{ backgroundColor: color }}
                                          title={color}
                                        />
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              </details>
                              
                              <div className="flex gap-2">
                                <Button size="sm" onClick={saveEdit} className="flex-1">
                                  <Check className="h-3 w-3 mr-1" />
                                  حفظ
                                </Button>
                                <Button size="sm" variant="outline" onClick={cancelEdit}>
                                  <X className="h-3 w-3" />
                                </Button>
                              </div>
                              
                              <div className="flex items-center gap-2 text-xs text-muted-foreground mt-2 pt-2 border-t">
                                <span>الموقع: ({Math.round(isEdited?.display_x ?? item.display_x)}, {Math.round(isEdited?.display_y ?? item.display_y)})</span>
                              </div>
                            </div>
                          )}
                          
                          {/* Edited indicator */}
                          {isEdited && !isEditing && (
                            <div className="absolute -top-6 right-0 bg-green-600 text-white text-xs px-1.5 py-0.5 rounded whitespace-nowrap flex items-center gap-1">
                              ✓ {isEdited.new_text?.substring(0, 15)}{isEdited.new_text?.length > 15 ? '...' : ''}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Side Panel */}
        <div className="space-y-4">
          {/* Instructions */}
          <Card className="bg-blue-500/5 border-blue-500/20">
            <CardContent className="pt-4">
              <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                <Edit3 className="h-4 w-4 text-blue-500" />
                طريقة التعديل
              </h4>
              <ol className="text-xs text-muted-foreground space-y-2 list-decimal list-inside">
                <li>ارفع ملف PDF</li>
                <li><strong>انقر على أي نص</strong> لتعديله</li>
                <li>اكتب النص الجديد واضغط Enter أو حفظ</li>
                <li>استخدم زر <strong>نقل</strong> لتحريك النص</li>
                <li>اضغط <strong>حفظ التعديلات</strong></li>
              </ol>
            </CardContent>
          </Card>

          {/* Edits List */}
          {editCount > 0 && (
            <Card className="bg-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">التعديلات ({editCount})</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 max-h-[300px] overflow-auto">
                {Object.values(editedItems).map(item => (
                  <div key={item.id} className="p-2 bg-green-500/10 rounded text-sm border border-green-500/20">
                    <div className="text-muted-foreground line-through text-xs">{item.text}</div>
                    <div className="text-green-700 font-medium">{item.new_text}</div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-6 text-xs mt-1"
                      onClick={() => {
                        const newEdited = { ...editedItems };
                        delete newEdited[item.id];
                        setEditedItems(newEdited);
                      }}
                    >
                      إلغاء التعديل
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Result */}
          {result && (
            <Card className="bg-green-500/10 border-green-500/30">
              <CardContent className="pt-4 space-y-2">
                <div className="flex items-center gap-2 text-green-600">
                  <Check className="h-5 w-5" />
                  <span className="font-medium">تم الحفظ بنجاح!</span>
                </div>
                <Button className="w-full" onClick={handleDownload}>
                  <Download className="h-4 w-4 mr-2" />
                  تحميل الملف المعدّل
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full" 
                  onClick={() => window.open(downloadModifiedPdf(result.file_name), '_blank')}
                >
                  فتح في نافذة جديدة
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
